// src/views/NotesView.js
import React, { useState } from 'react';
import { FileText, Plus, X } from 'lucide-react';

export default function NotesView({ notes, setNotes, themeClasses }) {
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNoteText, setNewNoteText] = useState("");
  
  const formatMarkdownText = (text) => {
    const lines = text.split('\n');
    return lines.map((line, idx) => {
      const isBullet = line.trim().startsWith('* ') || line.trim().startsWith('- ');
      const content = isBullet ? line.trim().substring(2) : line;
      const parts = content.split(/(\*\*.*?\*\*)/g);
      const elements = parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) return <strong key={i} className={`font-black ${themeClasses.textHead}`}>{part.slice(2, -2)}</strong>;
        return <span key={i}>{part}</span>;
      });
      if (isBullet) return <li key={idx} className="ml-4 list-disc mb-1">{elements}</li>;
      if (line.trim() === '') return <br key={idx} />;
      return <p key={idx} className="mb-2">{elements}</p>;
    });
  };

  return (
    <div className="max-w-4xl mx-auto space-y-4 sm:space-y-8 pb-12 h-full flex flex-col">
      <div className="shrink-0 mb-4">
        <h1 className={`text-2xl sm:text-3xl font-black ${themeClasses.textHead} flex items-center gap-3`}><FileText className="text-indigo-500" size={28}/> Research Notes</h1>
      </div>
      
      <div className={`${themeClasses.bgCard} border ${themeClasses.border} p-6 sm:p-8 rounded-3xl flex-1 flex flex-col shadow-xl min-h-[400px]`}>
        <div className="flex justify-between items-center mb-6 shrink-0">
           <h3 className="text-[10px] font-black text-indigo-500 uppercase tracking-widest flex items-center gap-2">Your Saved Insights</h3>
           <button onClick={() => setIsAddingNote(!isAddingNote)} className={`bg-indigo-600 text-white px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-indigo-500 transition-colors flex items-center gap-2`}><Plus size={14}/> New Note</button>
        </div>
        
        <div className="flex-1 overflow-y-auto custom-scrollbar space-y-4 pr-2">
           {isAddingNote && (
              <div className={`${themeClasses.input} p-4 rounded-2xl border border-indigo-500/50 flex flex-col gap-3 mb-4 shrink-0`}>
                  <textarea autoFocus value={newNoteText} onChange={e => setNewNoteText(e.target.value)} placeholder="Type a new research note..." className={`w-full bg-transparent text-sm ${themeClasses.textMain} outline-none resize-none`} rows={4} />
                  <div className="flex justify-end gap-3 mt-2">
                    <button onClick={() => setIsAddingNote(false)} className={`text-[10px] uppercase font-bold ${themeClasses.textMuted} hover:text-red-400`}>Cancel</button>
                    <button onClick={() => { if(newNoteText.trim()){ setNotes(prev => [newNoteText, ...prev]); setNewNoteText(""); setIsAddingNote(false); } }} className="text-[10px] uppercase font-bold text-indigo-500 bg-indigo-500/10 px-4 py-2 rounded-lg hover:bg-indigo-500 hover:text-white transition-colors">Save Note</button>
                  </div>
              </div>
            )}
            
           {notes.map((n, i) => (
              <div key={i} className={`${themeClasses.input} p-5 rounded-2xl border ${themeClasses.border} text-xs sm:text-sm leading-relaxed relative group transition-all hover:border-indigo-500/30`}>
                 <button onClick={() => setNotes(notes.filter((_, idx)=>idx!==i))} className={`absolute top-4 right-4 ${themeClasses.textMuted} hover:text-red-500 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity`}><X size={16}/></button>
                 <div className="pr-6 text-slate-300">{formatMarkdownText(n)}</div>
              </div>
           ))}
           {notes.length === 0 && !isAddingNote && (
             <div className="flex flex-col items-center justify-center h-full opacity-50">
                <FileText size={48} className={`mb-4 ${themeClasses.textMuted}`} />
                <p className={`text-center ${themeClasses.textMuted} text-xs font-black uppercase tracking-widest`}>No notes saved yet.</p>
             </div>
           )}
        </div>
      </div>
    </div>
  );
}